/**
 * 37. Quotations — list; editor; pricing/discounts; approval; PDF preview;
 * send; convert to order; rejection with reason.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
} from "@/components/ui";
import { useTranslation } from "@/lib/i18n";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { formatMoney, formatDate } from "@/lib/money";

interface QuotationRow {
  id: string;
  quotationNumber: string;
  customerName: string;
  quotationDate: string;
  validUntil: string | null;
  status: "Draft" | "Sent" | "Accepted" | "Converted" | "Rejected" | "Expired";
  total: string;
  currencyCode: string;
}

export function QuotationsPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<QuotationRow>(
    ["sales", "quotations"],
    "/sales/quotations",
  );

  return (
    <div>
      <PageHeader
        title="Quotations"
        subtitle="Pricing, discounts, approval, PDF preview, send, and convert to order."
        actions={
          <PendingButton variant="primary" reason="quoteEditor" slice="CRM-W06">
            {t("documentActions.newQuotation")}
          </PendingButton>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Quotations (${data.total})`}>
          <DataTable<QuotationRow>
            rows={data.items}
            columns={[
              {
                key: "quotationNumber",
                label: "Number",
                render: (r) => (
                  <span className="atlas-mono">{r.quotationNumber}</span>
                ),
              },
              { key: "customerName", label: "Customer" },
              {
                key: "quotationDate",
                label: "Date",
                render: (r) => formatDate(r.quotationDate),
              },
              {
                key: "validUntil",
                label: "Valid until",
                render: (r) => (r.validUntil ? formatDate(r.validUntil) : "—"),
              },
              {
                key: "total",
                label: "Total",
                numeric: true,
                render: (r) => formatMoney(r.total, r.currencyCode),
              },
              {
                key: "status",
                label: "Status",
                render: (r) => <StatusBadge status={r.status} />,
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
